import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getUserByEmail,
  ClientDTO,
  updateClientOwner,
  updateClientContactOwner,
  ClientContactDTO,
  ClientOwnerDTO,
} from 'wisestep-api';
import { WsButton, WsColors, WsDialog, WsTypography, WsTypoVariants } from 'wisestep-base';

import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

const ClientOwnerUpdateDialog = ({
  open,
  id,
  onClientSave,
  onClientContactSave,
  onCancel,
  clientOwner,
  entityType,
}: {
  open: boolean;
  id: string;
  clientOwner: ClientOwnerDTO | null;
  onClientSave?: (client: ClientDTO) => void;
  onClientContactSave?: (contact: ClientContactDTO) => void;
  onCancel: () => void;
  entityType: 'client' | 'contact';
}) => {
  const [isLoading, setLoading] = useState(false);
  const formMethods = useForm();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const onSubmit = async (data) => {
    if (!data.recruiterIds?.[0]) {
      setNotification(warning('Select a Valid Active User'));
      return;
    }
    setLoading(true);
    try {
      const recruiter = (await getUserByEmail(data.recruiterIds[0])).data;
      let response;
      if (entityType === 'client') {
        response = await updateClientOwner(id, {
          email: recruiter?.email || '',
          name: `${recruiter?.firstName || ''} ${recruiter?.lastName || ''}`.trim(),
        });
      } else {
        response = await updateClientContactOwner(id, {
          email: recruiter?.email || '',
          name: `${recruiter?.firstName || ''} ${recruiter?.lastName || ''}`.trim(),
        });
      }
      setNotification(success('Client Owner Updated Successfully !!'));
      response.data && onClientSave?.(response.data);
      response.data && onClientContactSave?.(response.data);
    } catch {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
    setLoading(false);
  };

  return (
    <WsDialog onClose={onCancel} openDialog={open}>
      <FormProvider {...formMethods}>
        <Box component="form" onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <DialogTitle>Update Owner</DialogTitle>
            <IconButton
              onClick={onCancel}
              sx={{
                color: WsColors.GreyBlack,
                mr: 2,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider orientation="horizontal" />
          <Box mt={2} ml={7}>
            <WsTypography variant={WsTypoVariants.BODY1}>Client Owner</WsTypography>
          </Box>
          <DialogContent sx={{ mb: 2, pl: 7, width: '80%' }}>
            <RecruiterSuggestions
              multiple={false}
              name="recruiterIds"
              defaultValue={clientOwner?.email ? [clientOwner?.email] : []}
              required
            />
          </DialogContent>
          <Divider orientation="horizontal" />
          <DialogActions sx={{ mr: 2, mb: 1 }}>
            <WsButton
              type="submit"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </WsButton>
          </DialogActions>
        </Box>
      </FormProvider>
    </WsDialog>
  );
};

export default ClientOwnerUpdateDialog;
